import type { AiConversationDetail, AiConversationListItem } from '@fos/shared/contracts/ai-history';
import type { ChatToolEvent } from '@fos/shared/contracts/copilot';
import { Archive, ArchiveRestore, ArrowLeft, MessagesSquare, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { formatDateTimeShort, formatRelativePast, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button, Checkbox, EmptyState, ErrorState, IconButton, Input, ListSkeleton, toast } from '../../../ui';
import { type ChatTurn, copilotChat, newTurnId } from '../chat-store';
import { resumeConversationId } from '../conversation-id';

/**
 * Historia rozmów z Copilotem (Fala 10, „historia promptów”): MOJE zapisane rozmowy (serwer: `history.conversations.*`,
 * prywatne — nikt inny ich nie czyta), wyszukiwanie w tytule i treści, archiwum, wznowienie: wiadomości wracają do
 * panelu (z działaniami i linkami, bez aktywnych podglądów do potwierdzenia), a kolejne polecenia dopisują się do tej
 * samej rozmowy.
 */

const PAGE = 20;

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Zdarzenie z zapisanej rozmowy: bez podglądów do potwierdzenia (ponowne potwierdzenie = drugi rekord). */
function readOnlyEvent(e: ChatToolEvent): ChatToolEvent {
  const { timePreview: _t, taskPreview: _k, delegatePreview: _d, choices: _c, plan: _p, notePreview: _n, ...rest } = e;
  return rest as ChatToolEvent;
}

/** Zapisana rozmowa → tury panelu (do czytania; kolejne polecenia idą do tej rozmowy). */
export function conversationTurns(detail: AiConversationDetail): ChatTurn[] {
  const turns: ChatTurn[] = detail.messages.map((m): ChatTurn => {
    if (m.role === 'user') return { id: newTurnId(), role: 'user', content: m.content, restored: true };
    return {
      id: newTurnId(),
      role: 'assistant',
      content: m.content,
      restored: true,
      result: {
        reply: m.content,
        state: m.state ?? 'done',
        toolEvents: m.toolEvents.map(readOnlyEvent),
        meta: {
          aiJobId: m.aiJobId,
          provider: m.meta?.provider ?? 'fake',
          model: m.meta?.model ?? null,
          simulated: m.meta?.simulated ?? false,
          source: 'ai',
          generatedAt: new Date(m.createdAt),
          contextVersion: null,
        },
        conversationId: detail.id,
      },
    };
  });
  turns.push({
    id: newTurnId(),
    role: 'note',
    content: `Wznowiono rozmowę z ${formatDateTimeShort(detail.lastMessageAt)}`,
  });
  return turns;
}

/**
 * Wznowienie zapisanej rozmowy: wiadomości do panelu + identyfikator rozmowy dla kolejnych poleceń. Zwraca funkcję
 * (wynik true = wznowiono). Używa jej lista rozmów i strona „Historia AI” (link „Wznów rozmowę”).
 */
export function useResumeConversation(): (id: string) => Promise<boolean> {
  const utils = trpc.useUtils();
  return async (id) => {
    try {
      const detail = await utils.history.conversations.get.fetch({ id });
      const first = detail.messages.find((m) => m.role === 'user')?.content ?? '';
      copilotChat.update(() => conversationTurns(detail));
      resumeConversationId(detail.id, first);
      return true;
    } catch (err) {
      toast.error(errorMessage(err, 'Nie udało się otworzyć rozmowy.'));
      return false;
    }
  };
}

function ConversationRow({
  item,
  onResume,
  busy,
}: {
  item: AiConversationListItem;
  onResume: (item: AiConversationListItem) => void;
  busy: boolean;
}) {
  const utils = trpc.useUtils();
  const archive = trpc.history.conversations.archive.useMutation();
  const now = new Date();
  const archived = item.archivedAt !== null;
  const toggleArchive = async () => {
    try {
      await archive.mutateAsync({ id: item.id, archived: !archived });
      void utils.history.conversations.list.invalidate();
      toast.success(archived ? 'Przywrócono rozmowę' : 'Przeniesiono rozmowę do archiwum');
    } catch (err) {
      toast.error(errorMessage(err, 'Nie udało się zmienić rozmowy.'));
    }
  };
  return (
    <li className="flex items-start gap-2 border-b border-line last:border-b-0">
      <button
        type="button"
        disabled={busy}
        onClick={() => onResume(item)}
        className="flex min-w-0 flex-1 flex-col gap-0.5 py-2.5 text-left hover:text-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
      >
        <span className="line-clamp-2 text-body-s font-medium break-words text-fg">{item.title}</span>
        <span className="text-caption text-fg-muted">
          {formatRelativePast(item.lastMessageAt, now)} · {item.messageCount}{' '}
          {plural(item.messageCount, ['wiadomość', 'wiadomości', 'wiadomości'])}
          {item.contextLabel ? ` · ${item.contextLabel}` : ''}
          {archived ? ' · w archiwum' : ''}
        </span>
      </button>
      <IconButton
        icon={archived ? ArchiveRestore : Archive}
        label={archived ? 'Przywróć rozmowę' : 'Przenieś rozmowę do archiwum'}
        size={28}
        iconSize={16}
        className="mt-2"
        onClick={() => void toggleArchive()}
      />
    </li>
  );
}

export function ConversationHistory({ onClose, onResumed }: { onClose: () => void; onResumed?: () => void }) {
  const resumeConversation = useResumeConversation();
  const [q, setQ] = useState('');
  const [archived, setArchived] = useState(false);
  const [resuming, setResuming] = useState<string | null>(null);
  const query = useDebounced(q.trim());
  const list = trpc.history.conversations.list.useInfiniteQuery(
    { limit: PAGE, ...(query ? { q: query } : {}), ...(archived ? { includeArchived: true } : {}) },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  const resume = async (item: AiConversationListItem) => {
    setResuming(item.id);
    const ok = await resumeConversation(item.id);
    setResuming(null);
    if (!ok) return;
    onResumed?.();
    onClose();
  };

  return (
    <section aria-label="Historia rozmów" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <IconButton icon={ArrowLeft} label="Wróć do rozmowy" size={28} iconSize={16} onClick={onClose} />
        <h3 className="text-body-s font-semibold text-fg">Moje rozmowy</h3>
      </div>
      <p className="text-caption text-fg-muted">Rozmowy widzisz tylko Ty. Kliknij, żeby wznowić.</p>
      <div className="relative">
        <Search
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted"
        />
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj w rozmowach…"
          aria-label="Szukaj w rozmowach"
          className="pl-9"
        />
      </div>
      <Checkbox
        checked={archived}
        onCheckedChange={(v) => setArchived(Boolean(v))}
        label="Pokaż archiwum"
        className="text-caption"
      />
      {list.isPending ? (
        <ListSkeleton rows={4} />
      ) : list.isError ? (
        <ErrorState compact onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={MessagesSquare}
          title={query ? 'Nic nie znaleziono.' : 'Brak zapisanych rozmów.'}
          description={query ? undefined : 'Rozmowy z Copilotem zapisują się same — wrócisz do nich tutaj.'}
        />
      ) : (
        <>
          <ul className="flex flex-col" aria-label="Zapisane rozmowy">
            {items.map((item) => (
              <ConversationRow key={item.id} item={item} busy={resuming !== null} onResume={(i) => void resume(i)} />
            ))}
          </ul>
          {list.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="s"
                onClick={() => void list.fetchNextPage()}
                loading={list.isFetchingNextPage}
              >
                Pokaż starsze
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
